import React from "react";
import styled from "styled-components";
import { Collapse } from "antd";
import {
  Icon,
  IconContainer,
} from "../../../components/visuals/styledComponents";
import downArrow from "../../../assets/svg/down_arrow_icon.svg";
import {
  darkGreen,
  lightGreys,
  ESCALATION_COLORS,
  greyText,
} from "../../../styles";
import { MESSAGES } from "../../../messages";
import {
  FlexibleContainer,
  RowContainer,
} from "../../../components/containers/styledComponents";
import { CompassParagraph } from "../../../components/common/styledComponents";
import { Checkbox } from "../../../components/visuals/Checkbox";
import { CompassTag, TagProps } from "../../../components/visuals/CompassTag";
import { EngagementRecord } from "../../../models/stewardshipModels";
import { buildEscalations } from "../utils";
import {
  EscalationKey,
  EscalationParent,
  EscalationType,
  EscalationParentType,
} from "../../../enums/Escalations";

// Re-export types for backward compatibility
export type { EscalationParentType };

const RotatingIconContainer = styled(IconContainer)<{ isActive: boolean }>`
  transition: transform 0.3s;
  transform: rotate(${({ isActive }): number => (isActive ? 0 : -90)}deg);
`;

const StyledCollapse = styled(Collapse)`
  font-family: "Apercu" !important; /* Change font */
  .ant-collapse-header {
    display: flex !important;
    color: ${darkGreen};
    justify-content: flex-start;
    align-items: center !important;
    padding: 0 !important;
  }

  .ant-collapse-content {
    background-color: white !important;
  }
`;

const Caret = ({ isActive }: { isActive: boolean }): JSX.Element => (
  <RotatingIconContainer isActive={isActive}>
    <Icon src={downArrow} />
  </RotatingIconContainer>
);

// EscalationKey, EscalationParent, EscalationType, EscalationParentType are imported from enums/Escalations.ts

const escalationDescriptions =
  MESSAGES.STEWARDSHIP.ENGAGEMENT_MODULE.ESCALATION_MESSAGES;

const DEFAULT_ESCALATIONS_CONFIG: Record<EscalationKey, EscalationType> = {
  E1_1: {
    key: EscalationKey.E1_1,
    description: escalationDescriptions.E1.E1_1,
    isActive: false,
  },
  E1_2: {
    key: EscalationKey.E1_2,
    description: escalationDescriptions.E1.E1_2,
    isActive: false,
  },
  E1_3: {
    key: EscalationKey.E1_3,
    description: escalationDescriptions.E1.E1_3,
    isActive: false,
  },
  E1_4: {
    key: EscalationKey.E1_4,
    description: escalationDescriptions.E1.E1_4,
    isActive: false,
  },
  E2_1: {
    key: EscalationKey.E2_1,
    description: escalationDescriptions.E2.E2_1,
    isActive: false,
  },
  E2_2: {
    key: EscalationKey.E2_2,
    description: escalationDescriptions.E2.E2_2,
    isActive: false,
  },
  E2_3: {
    key: EscalationKey.E2_3,
    description: escalationDescriptions.E2.E2_3,
    isActive: false,
  },
  E2_4: {
    key: EscalationKey.E2_4,
    description: escalationDescriptions.E2.E2_4,
    isActive: false,
  },
  E2_5: {
    key: EscalationKey.E2_5,
    description: escalationDescriptions.E2.E2_5,
    isActive: false,
  },
  E2_6: {
    key: EscalationKey.E2_6,
    description: escalationDescriptions.E2.E2_6,
    isActive: false,
  },
  E3_1: {
    key: EscalationKey.E3_1,
    description: escalationDescriptions.E3.E3_1,
    isActive: false,
  },
  E3_2: {
    key: EscalationKey.E3_2,
    description: escalationDescriptions.E3.E3_2,
    isActive: false,
  },
  E3_3: {
    key: EscalationKey.E3_3,
    description: escalationDescriptions.E3.E3_3,
    isActive: false,
  },
  E3_4: {
    key: EscalationKey.E3_4,
    description: escalationDescriptions.E3.E3_4,
    isActive: false,
  },
  E3_5: {
    key: EscalationKey.E3_5,
    description: escalationDescriptions.E3.E3_5,
    isActive: false,
  },
  E3_6: {
    key: EscalationKey.E3_6,
    description: escalationDescriptions.E3.E3_6,
    isActive: false,
  },
  E3_7: {
    key: EscalationKey.E3_7,
    description: escalationDescriptions.E3.E3_7,
    isActive: false,
  },
  E3_8: {
    key: EscalationKey.E3_8,
    description: escalationDescriptions.E3.E3_8,
    isActive: false,
  },
  E4_1: {
    key: EscalationKey.E4_1,
    description: escalationDescriptions.E4.E4_1,
    isActive: false,
  },
  E4_2: {
    key: EscalationKey.E4_2,
    description: escalationDescriptions.E4.E4_2,
    isActive: false,
  },
  E4_3: {
    key: EscalationKey.E4_3,
    description: escalationDescriptions.E4.E4_3,
    isActive: false,
  },
  E5_1: {
    key: EscalationKey.E5_1,
    description: escalationDescriptions.E5.E5_1,
    isActive: false,
  },
  E5_2: {
    key: EscalationKey.E5_2,
    description: escalationDescriptions.E5.E5_2,
    isActive: false,
  },
};

export const DEFAULT_PARENT_ESCALATIONS: EscalationParentType[] = [
  {
    key: 1,
    title: EscalationParent.E1,
    color: ESCALATION_COLORS[1],
    escalations: [
      DEFAULT_ESCALATIONS_CONFIG.E1_1,
      DEFAULT_ESCALATIONS_CONFIG.E1_2,
      DEFAULT_ESCALATIONS_CONFIG.E1_3,
      DEFAULT_ESCALATIONS_CONFIG.E1_4,
    ],
  },
  {
    key: 2,
    title: EscalationParent.E2,
    color: ESCALATION_COLORS[2],
    escalations: [
      DEFAULT_ESCALATIONS_CONFIG.E2_1,
      DEFAULT_ESCALATIONS_CONFIG.E2_2,
      DEFAULT_ESCALATIONS_CONFIG.E2_3,
      DEFAULT_ESCALATIONS_CONFIG.E2_4,
      DEFAULT_ESCALATIONS_CONFIG.E2_5,
      DEFAULT_ESCALATIONS_CONFIG.E2_6,
    ],
  },
  {
    key: 3,
    title: EscalationParent.E3,
    color: ESCALATION_COLORS[3],
    escalations: [
      DEFAULT_ESCALATIONS_CONFIG.E3_1,
      DEFAULT_ESCALATIONS_CONFIG.E3_2,
      DEFAULT_ESCALATIONS_CONFIG.E3_3,
      DEFAULT_ESCALATIONS_CONFIG.E3_4,
      DEFAULT_ESCALATIONS_CONFIG.E3_5,
      DEFAULT_ESCALATIONS_CONFIG.E3_6,
      DEFAULT_ESCALATIONS_CONFIG.E3_7,
      DEFAULT_ESCALATIONS_CONFIG.E3_8,
    ],
  },
  {
    key: 4,
    title: EscalationParent.E4,
    color: ESCALATION_COLORS[4],
    escalations: [
      DEFAULT_ESCALATIONS_CONFIG.E4_1,
      DEFAULT_ESCALATIONS_CONFIG.E4_2,
      DEFAULT_ESCALATIONS_CONFIG.E4_3,
    ],
  },
  {
    key: 5,
    title: EscalationParent.E5,
    color: ESCALATION_COLORS[5],
    escalations: [
      DEFAULT_ESCALATIONS_CONFIG.E5_1,
      DEFAULT_ESCALATIONS_CONFIG.E5_2,
    ],
  },
];

const EscalationRows: React.FC<{
  escalations: EscalationType[];
}> = ({ escalations }): JSX.Element => {
  return (
    <RowContainer wrap="wrap" ai="center" jc="flex-start">
      {escalations.map((escalation) => (
        <FlexibleContainer
          key={escalation.key}
          ai="center"
          p="10px 20px"
          bg={lightGreys.KPIBackground}
          br="6px"
          w="350px"
          mr="12px"
          mb="12px"
        >
          <RowContainer>
            <CompassParagraph fontSize={12} ml={10}>
              {escalation.description}
            </CompassParagraph>
          </RowContainer>
          <Checkbox checked={escalation.isActive} />
        </FlexibleContainer>
      ))}
    </RowContainer>
  );
};

const BannerHeader: React.FC<{ escalation: EscalationParentType }> = ({
  escalation,
}) => {
  const activeCount = escalation.escalations.filter((e) => e.isActive).length;
  const tagProps: TagProps = {
    tagKey: escalation.key.toString(),
    bg: escalation.color,
    color: darkGreen,
    displayValue: `Escalation ${escalation.key}`,
    shouldDisplayKey: false,
    isClickable: false,
  };
  return (
    <RowContainer jc="flex-start" ai="center">
      <CompassTag {...tagProps} />
      <CompassParagraph fontSize={12}>{escalation.title}</CompassParagraph>
      <CompassParagraph
        fontSize={10}
        style={{
          marginLeft: 8,
          padding: "2px 8px",
          borderRadius: 10,
          backgroundColor: escalation.color,
          color: greyText,
          fontWeight: 600,
        }}
      >
        {activeCount} active
      </CompassParagraph>
    </RowContainer>
  );
};

export const EscalationAccordion: React.FC<{
  engagement: EngagementRecord | null;
}> = ({ engagement }) => {
  const escalations = engagement ? buildEscalations(engagement) : [];

  return (
    <FlexibleContainer fd="column" w="100%" p="10px 40px" ofy="auto">
      {escalations.map((parentEscalation: EscalationParentType) => (
        <RowContainer key={parentEscalation.key}>
          <StyledCollapse
            key={parentEscalation.key}
            expandIcon={({ isActive }): JSX.Element => (
              <Caret isActive={!!isActive} />
            )}
            style={{
              width: "100%",
              backgroundColor: lightGreys.KPIBackground,
            }}
            items={[
              {
                key: parentEscalation.key,
                label: <BannerHeader escalation={parentEscalation} />,
                children: (
                  <EscalationRows escalations={parentEscalation.escalations} />
                ),
              },
            ]}
          />
        </RowContainer>
      ))}
    </FlexibleContainer>
  );
};
